import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MapPin, Phone, Share2, User as UserIcon } from 'lucide-react';
import { Card, Button, Badge, BloodTypeBadge, ComponentBadge, COMPONENT_LABELS, UrgencyBadge, Spinner, Alert, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';
import { requestShareText, whatsappShareUrl } from '../utils/share';
import { useAuth } from '../context/AuthContext';

const CLOSED_NOTICE = {
  fulfilled: 'This request has been fulfilled — no more donors are needed.',
  expired: 'This request expired and is no longer accepting responses.',
  cancelled: 'This request was cancelled by the requester.',
};

function timeUntil(expiresAt) {
  if (!expiresAt) return null;
  const diffMs = new Date(expiresAt) - new Date();
  if (diffMs <= 0) return 'Expired';
  const hrs = Math.floor(diffMs / 3600000);
  if (hrs < 1) return `${Math.floor(diffMs / 60000)} min left`;
  if (hrs < 24) return `${hrs}h left`;
  return `${Math.floor(hrs / 24)}d left`;
}

export default function RequestDetail() {
  const { id } = useParams();
  const { user, becomeDonor } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [request, setRequest] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  const [myResponse, setMyResponse] = useState(null);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [confirmingAccept, setConfirmingAccept] = useState(false);
  const [responding, setResponding] = useState(false);
  const [respondError, setRespondError] = useState('');
  const [respondedNow, setRespondedNow] = useState(null);

  const [responders, setResponders] = useState(null);
  const [confirmingDonation, setConfirmingDonation] = useState(false);
  const [donationError, setDonationError] = useState('');
  const [confirmingResponderId, setConfirmingResponderId] = useState(null);

  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');

  const isDonor = user?.role === 'donor';
  const isOwner = request && user?.id === request.requester_id;
  // Requests reached from an old notification or a shared link can be long
  // closed — the list page only ever shows open ones, so this is the only
  // place a lapsed request surfaces with action buttons attached.
  const isOpen = request?.status === 'open';
  const hasActiveResponse = ['accepted', 'donated', 'completed'].includes(myResponse?.status);
  const returnUrl = `/requests/${id}`;

  useEffect(() => {
    setLoading(true);
    api.get(`/requests/${id}`)
      .then(r => setRequest(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!request || !isDonor) return;
    api.get(`/requests/${id}/my-response`)
      .then(r => setMyResponse(r.data))
      .catch(e => {
        // No DonorProfile yet (e.g. donor account created but setup never finished)
        if (e.response?.status === 404) setNeedsProfile(true);
        setMyResponse(null);
      });
  }, [id, request, isDonor]);

  const loadResponders = useCallback(() => {
    if (!request || !isOwner) return;
    api.get(`/requests/${id}/responses`)
      .then(r => setResponders(r.data))
      .catch(() => setResponders([]));
  }, [id, request, isOwner]);

  useEffect(() => { loadResponders(); }, [loadResponders]);

  const upgradeToDonor = async () => {
    setUpgrading(true);
    setUpgradeError('');
    try {
      await becomeDonor();
      navigate(`/donor/setup?redirect=${encodeURIComponent(returnUrl)}`);
    } catch (e) {
      setUpgradeError(e.response?.data?.detail || 'Could not switch your account to a donor account.');
      setUpgrading(false);
    }
  };

  const submitResponse = async (status) => {
    setResponding(true);
    setRespondError('');
    try {
      await api.patch(`/requests/${id}/respond`, { status });
      setMyResponse({ status, responded_at: new Date().toISOString() });
      setRespondedNow(status);
      setConfirmingAccept(false);
    } catch (e) {
      setRespondError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  const submitDonorConfirm = async () => {
    setConfirmingDonation(true);
    setDonationError('');
    try {
      const r = await api.post(`/requests/${id}/confirm-donation`);
      setMyResponse(prev => ({ ...prev, status: r.data.status, donor_confirmed_at: r.data.donor_confirmed_at }));
    } catch (e) {
      setDonationError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setConfirmingDonation(false);
    }
  };

  const confirmResponderDonation = async (responseId) => {
    setConfirmingResponderId(responseId);
    try {
      await api.post(`/requests/${id}/responses/${responseId}/confirm`);
      loadResponders();
    } catch (e) {
      // surfaced inline via alert since it's a per-row action
      alert(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setConfirmingResponderId(null);
    }
  };

  if (loading) return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Spinner /></div>
    </PageLayout>
  );

  if (notFound || !request) return (
    <PageLayout>
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: '36px', marginBottom: '10px', opacity: 0.6 }}>🩸</div>
          <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '4px' }}>Request not found</p>
          <p style={{ fontSize: '13px', color: 'var(--gray-400)', marginBottom: '16px' }}>It may have been removed or the link is incorrect.</p>
          <Link to="/requests"><Button variant="primary" size="sm">Back to requests</Button></Link>
        </div>
      </Card>
    </PageLayout>
  );

  const expiry = timeUntil(request.expires_at);
  const donationPhrase = request.component && request.component !== 'whole_blood'
    ? `${request.blood_type} ${COMPONENT_LABELS[request.component]}`
    : request.blood_type;

  return (
    <PageLayout>
      <div style={{ animation: 'bb-fadeUp 0.5s ease both' }}>
      <div style={{ marginBottom: '16px' }}>
        <Link to="/requests" style={{ fontSize: '13px', color: 'var(--gray-500)', textDecoration: 'none' }}>← Back to requests</Link>
      </div>

      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '14px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BloodTypeBadge type={request.blood_type} />
            <div>
              <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--gray-900)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {request.hospital_name || (user ? '—' : 'Login to view hospital')}
                <ComponentBadge component={request.component} />
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <MapPin size={12} color="var(--gray-400)" />
                {request.hospital_address ? `${request.hospital_address}, ` : ''}{request.city}
              </div>
            </div>
          </div>
          <UrgencyBadge urgency={request.urgency} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: '10px', marginBottom: request.notes ? '14px' : 0 }}>
          {[
            ['Units needed', request.units_needed],
            ['Patient', request.patient_name || (user ? '—' : 'Login to view')],
            ['Status', request.status],
            ['Time left', expiry || '—'],
          ].map(([label, value]) => (
            <div key={label} style={{ padding: '11px 12px', background: 'var(--gray-50)', borderRadius: '9px', border: '1px solid var(--gray-100)' }}>
              <div style={{ fontSize: '10.5px', color: 'var(--gray-400)', fontWeight: 600, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--gray-900)' }}>{value}</div>
            </div>
          ))}
        </div>

        {request.notes && (
          <div style={{ fontSize: '13px', color: 'var(--gray-600)', lineHeight: 1.6, paddingTop: '4px' }}>
            <span style={{ fontWeight: 600, color: 'var(--gray-700)' }}>Notes: </span>{request.notes}
          </div>
        )}

        {/* Sits on the request itself, not in the donor card, so the requester,
            organizations and logged-out visitors can all forward it — the
            people most likely to know a matching donor personally. Hidden once
            the request closes, so nobody chases a need that's already met. */}
        {isOpen && (
          <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid var(--gray-100)' }}>
            <a
              href={whatsappShareUrl(requestShareText(request))}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', display: 'inline-block' }}
            >
              <Button variant="secondary" size="sm">
                <Share2 size={14} /> Share on WhatsApp
              </Button>
            </a>
            <span style={{ fontSize: '12px', color: 'var(--gray-500)', marginLeft: '10px' }}>
              Shares the blood type, city and link — never the patient or hospital name.
            </span>
          </div>
        )}
      </Card>

      {/* Closed request — say so once, up front, for everyone */}
      {!isOpen && (
        <Card style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-700)' }}>
            {CLOSED_NOTICE[request.status] || 'This request is closed.'}
          </div>
          <div style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginTop: '4px' }}>
            <Link to="/requests" style={{ color: 'var(--red)', fontWeight: 600 }}>Browse open requests →</Link>
          </div>
        </Card>
      )}

      {/* Donor response section */}
      {!user && isOpen && (
        <Card style={{
          marginBottom: '16px',
          background: 'linear-gradient(135deg, var(--red-deeper) 0%, var(--red) 100%)',
          border: 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '4px' }}>Can you donate {donationPhrase}?</div>
              <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.8)' }}>
                Register in under a minute — we'll bring you straight back here to respond.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <Link to={`/login?redirect=${encodeURIComponent(returnUrl)}`}>
                <Button size="sm" variant="ghost" style={{ color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>Sign in</Button>
              </Link>
              <Link to={`/register?redirect=${encodeURIComponent(returnUrl)}&role=donor`}>
                <Button size="sm" variant="secondary">Register to respond</Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {/* Recipient looking at an open request — they can help too, and don't
          need a second account to do it. Owners get the responses card instead. */}
      {user?.role === 'recipient' && isOpen && !isOwner && (
        <Card style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '6px' }}>
            Can you donate {donationPhrase}?
          </div>
          <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '14px', lineHeight: 1.5 }}>
            Your account is set up for requesting blood. Switch it to a donor account to respond —
            no new signup, and you keep the requests you've already posted.
          </p>
          {upgradeError && <Alert variant="error">{upgradeError}</Alert>}
          <Button variant="primary" loading={upgrading} onClick={upgradeToDonor}>Become a donor →</Button>
        </Card>
      )}

      {isDonor && needsProfile && isOpen && (
        <Card style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '6px' }}>Finish your donor profile to respond</div>
          <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '14px', lineHeight: 1.5 }}>
            Just a few quick details (blood type, age, weight) — takes under a minute.
          </p>
          <Link to={`/donor/setup?redirect=${encodeURIComponent(returnUrl)}`}>
            <Button variant="primary">Complete profile →</Button>
          </Link>
        </Card>
      )}

      {/* Once the request is closed the only reason to keep this card is a donor
          who is mid-flow — the requester confirming flips the request to
          fulfilled, so the donor must still be able to see how it ended. */}
      {isDonor && !needsProfile && (isOpen || hasActiveResponse) && (
        <Card style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '12px' }}>
            {isOpen ? 'Can you donate?' : 'Your donation'}
          </div>

          {respondError && <Alert variant="error">{respondError}</Alert>}

          {myResponse?.status === 'completed' ? (
            <div style={{ padding: '13px 16px', borderRadius: '10px', background: 'var(--green-light)', border: '1px solid #A7DFB9' }}>
              <div style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--green-dark)' }}>✓ Donation confirmed — thank you!</div>
              <div style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginTop: '4px' }}>
                The requester confirmed it. It's now on your donor history — your certificate is in{' '}
                <Link to="/donor/certificates" style={{ color: 'var(--green-dark)', fontWeight: 600 }}>My Certificates</Link>.
              </div>
            </div>
          ) : myResponse?.status === 'donated' ? (
            <div style={{ padding: '13px 16px', borderRadius: '10px', background: 'var(--gray-100)', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--gray-700)' }}>Waiting for requester confirmation</div>
              <div style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginTop: '4px' }}>
                You reported that you donated. Once the requester confirms it, it'll count toward your donor
                history and your certificate will be issued.
              </div>
            </div>
          ) : myResponse?.status === 'accepted' ? (
            <div style={{
              padding: '13px 16px', borderRadius: '10px',
              background: 'var(--green-light)', border: '1px solid #A7DFB9',
            }}>
              {donationError && <Alert variant="error">{donationError}</Alert>}
              <div style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--green-dark)' }}>✓ You accepted this request</div>
              {respondedNow && (
                <div style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginTop: '4px', marginBottom: '10px' }}>
                  The requester has been notified and can now see your contact info.
                </div>
              )}
              <Button
                variant="primary" size="sm" loading={confirmingDonation}
                style={{ marginTop: '10px' }}
                onClick={submitDonorConfirm}
              >
                I've donated
              </Button>
            </div>
          ) : myResponse?.status === 'declined' ? (
            <div style={{ padding: '13px 16px', borderRadius: '10px', background: 'var(--gray-100)', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--gray-700)' }}>You declined this request</div>
              {respondedNow && (
                <div style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginTop: '4px' }}>Thanks for letting them know.</div>
              )}
            </div>
          ) : confirmingAccept ? (
            <div style={{ padding: '13px 16px', borderRadius: '10px', background: 'var(--gray-50)', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: '13.5px', color: 'var(--gray-700)', marginBottom: '12px' }}>
                {`Confirm you can donate ${donationPhrase} for this request? Your name and phone will be shared with the requester.`}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant="primary"
                  size="sm"
                  loading={responding}
                  onClick={() => submitResponse('accepted')}
                >
                  Yes, confirm
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setConfirmingAccept(false)} disabled={responding}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button variant="primary" onClick={() => setConfirmingAccept(true)}>I can donate</Button>
          )}
        </Card>
      )}

      {/* Owner: who responded */}
      {isOwner && (
        <Card>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '12px' }}>Responses</div>
          {responders === null ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}><Spinner /></div>
          ) : responders.length === 0 ? (
            <p style={{ fontSize: '13px', color: 'var(--gray-400)' }}>No responses yet. We'll notify donors nearby.</p>
          ) : responders.map((r, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 0',
              borderBottom: i < responders.length - 1 ? '1px solid var(--gray-100)' : 'none',
            }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%', background: 'var(--gray-100)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <UserIcon size={16} color="var(--gray-500)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)' }}>{r.donor_name}</div>
                {(r.status === 'accepted' || r.status === 'donated' || r.status === 'completed') && (
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                    <Phone size={11} color="var(--gray-400)" />{r.phone}
                  </div>
                )}
              </div>
              <BloodTypeBadge type={r.blood_type} />
              {r.status === 'donated' ? (
                <Button
                  variant="primary" size="sm"
                  loading={confirmingResponderId === r.id}
                  onClick={() => confirmResponderDonation(r.id)}
                >
                  Confirm donation
                </Button>
              ) : r.status === 'completed' ? (
                <Badge color="green">✓ Confirmed</Badge>
              ) : (
                <Badge color={r.status === 'accepted' ? 'green' : 'gray'}>{r.status}</Badge>
              )}
            </div>
          ))}
        </Card>
      )}
      </div>
    </PageLayout>
  );
}
